import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MagnifyingGlass, ArrowRight, CircleNotch, ArrowsDownUp, Heartbeat } from "@phosphor-icons/react";
import { HealthScoreChip, CriticalAlertBar } from "@/components/admin/HealthScore";

const STATUS_DOT = {
  active:    "bg-emerald-400",
  trial:     "bg-amber-400",
  suspended: "bg-rose-400",
  cancelled: "bg-slate-500",
};

const SORT_OPTIONS = [
  { value: "margin", label: "Margem (piores primeiro)" },
  { value: "cost",   label: "Custo IA (maior primeiro)" },
  { value: "mrr",    label: "Receita (maior primeiro)" },
  { value: "health", label: "Saúde (piores primeiro)" },
  { value: "recent", label: "Mais recentes" },
  { value: "name",   label: "Nome (A→Z)" },
];

function fmtBRL(cents) {
  return ((cents || 0) / 100).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 0,
  });
}

function fmtBRLn(brl) {
  return Number(brl || 0).toLocaleString("pt-BR", {
    style: "currency", currency: "BRL", maximumFractionDigits: 0,
  });
}

function fmtTokens(n) {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)} mi`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)} k`;
  return v.toString();
}

function timeAgo(iso) {
  if (!iso) return "—";
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  return `${d}d`;
}

export default function AdminTenants() {
  const [data, setData] = useState(null); // {items, health_counts}
  const [search, setSearch] = useState("");
  const [healthFilter, setHealthFilter] = useState(null);
  const [sortKey, setSortKey] = useState("margin");
  const [days, setDays] = useState(30);
  // Default: hide internal/dogfooding tenants ("AgentUS Operações" etc.)
  // from the customer list & financial aggregates. Super admin can
  // toggle on to audit them.
  const [includeInternal, setIncludeInternal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setData(null);
    const params = new URLSearchParams({ days, sort: sortKey });
    if (search) params.set("search", search);
    if (healthFilter) params.set("health_level", healthFilter);
    if (includeInternal) params.set("include_internal", "true");
    const tid = setTimeout(() => {
      api.get(`/admin/tenants?${params}`).then(({ data }) => setData(data));
    }, 200);
    return () => clearTimeout(tid);
  }, [search, healthFilter, sortKey, days, includeInternal]);

  const items = data?.items;
  const healthCounts = data?.health_counts;

  const totals = useMemo(() => {
    if (!items) return { msg: 0, ai: 0, cost: 0, mrr: 0, packs: 0, margin: 0, tokens: 0 };
    return items.reduce((acc, t) => ({
      msg:    acc.msg    + (t.messages_total || 0),
      ai:     acc.ai     + (t.ai_calls || 0),
      cost:   acc.cost   + (t.cost_brl || 0),
      mrr:    acc.mrr    + (t.mrr_cents || 0),
      packs:  acc.packs  + (t.packs_revenue_cents || 0),
      margin: acc.margin + (t.margin_brl || 0),
      tokens: acc.tokens + (t.tokens_total || 0),
    }), { msg: 0, ai: 0, cost: 0, mrr: 0, packs: 0, margin: 0, tokens: 0 });
  }, [items]);

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">Plataforma</div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1">Clientes</h1>
          <p className="text-sm text-slate-400 mt-1.5">
            {items === undefined ? "Carregando…" : `${items?.length || 0} clientes · `}
            {items && (
              <>
                Base {fmtBRL(totals.mrr)}
                {totals.packs > 0 && <> + pacotes {fmtBRL(totals.packs)}</>}
                {" "}− custo {fmtBRLn(totals.cost)} = <span className={totals.margin >= 0 ? "text-emerald-400" : "text-rose-400"}>margem {fmtBRLn(totals.margin)}</span> · {fmtTokens(totals.tokens)} tokens
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1 text-[12px]">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`tenants-days-${d}`}
              className={`px-3 py-1.5 rounded-md transition-colors ${
                days === d ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </header>

      {/* Critical alert bar */}
      {items && (
        <CriticalAlertBar
          counts={healthCounts}
          total={items.length}
          currentFilter={healthFilter}
          onFilterCritical={setHealthFilter}
        />
      )}

      {/* Filters */}
      <div className="bg-white/[0.03] border border-white/10 rounded-lg p-3 flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex-1 min-w-[240px] flex items-center gap-2">
          <MagnifyingGlass size={14} weight="regular" className="text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome do tenant…"
            data-testid="tenants-search"
            className="bg-transparent flex-1 text-sm text-white placeholder:text-slate-600 focus:outline-none"
          />
        </div>

        <div className="flex items-center gap-1.5">
          <Heartbeat size={12} weight="regular" className="text-slate-500" />
          <select
            value={healthFilter || ""}
            onChange={(e) => setHealthFilter(e.target.value || null)}
            data-testid="tenants-health-filter"
            className="bg-white/5 border border-white/10 rounded-md text-[12px] text-white px-2.5 py-1.5 focus:outline-none focus:border-white/30"
          >
            <option value="">Toda saúde</option>
            <option value="critical">🔴 Crítico</option>
            <option value="attention">🟡 Atenção</option>
            <option value="healthy">🟢 Saudável</option>
          </select>
        </div>

        <div className="flex items-center gap-1.5">
          <ArrowsDownUp size={12} weight="regular" className="text-slate-500" />
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            data-testid="tenants-sort"
            className="bg-white/5 border border-white/10 rounded-md text-[12px] text-white px-2.5 py-1.5 focus:outline-none focus:border-white/30"
          >
            {SORT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        {/* Toggle: incluir tenants internos (dogfooding / contas
            internas do super_admin). Default false pra não inflar o
            financeiro com "AgentUS Operações" etc. */}
        <label
          className="flex items-center gap-2 text-[12px] text-slate-400 cursor-pointer select-none"
          title="Tenants internos (dogfooding) ficam ocultos por padrão pra não poluir o financeiro."
        >
          <input
            type="checkbox"
            checked={includeInternal}
            onChange={(e) => setIncludeInternal(e.target.checked)}
            data-testid="tenants-include-internal"
            className="accent-sky-400"
          />
          mostrar tenants internos
        </label>
      </div>

      {/* Table */}
      <div className="bg-white/[0.03] border border-white/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02] text-left">
              <tr>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Saúde</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Empresa</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Status</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">WhatsApp</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Msgs</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Tokens</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Plano base</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Pacotes</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Custo IA</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium text-right">Margem</th>
                <th className="px-4 py-2.5 text-[10px] uppercase tracking-[0.16em] text-slate-500 font-medium">Último acesso</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {items === undefined && (
                <tr><td colSpan="11" className="p-12 text-center">
                  <CircleNotch size={20} weight="regular" className="text-slate-500 animate-spin inline-block" />
                </td></tr>
              )}
              {items?.length === 0 && (
                <tr><td colSpan="11" className="p-12 text-center text-slate-500 text-sm">
                  Nenhum cliente encontrado com os filtros atuais.
                </td></tr>
              )}
              {items?.map((t) => {
                const margin = t.margin_brl ?? 0;
                const isLoss = (t.mrr_cents || 0) > 0 && margin < 0;
                return (
                <tr
                  key={t.id}
                  onClick={() => navigate(`/admin-master/tenants/${t.id}`)}
                  data-testid={`tenant-row-${t.id}`}
                  className={`border-t border-white/5 hover:bg-white/[0.04] cursor-pointer transition-colors ${
                    isLoss ? "bg-rose-500/[0.06]"
                      : t.health?.level === "critical" ? "bg-rose-500/[0.03]" : ""
                  }`}
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <HealthScoreChip health={t.health} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-white font-medium flex items-center gap-2">
                      {t.name}
                      {isLoss && (
                        <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 font-semibold">prejuízo</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500">{t.sector}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-300">
                      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[t.status] || "bg-slate-500"}`} />
                      {t.status || "active"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[12px] ${
                      t.whatsapp_status === "connected" ? "text-emerald-400"
                        : t.whatsapp_status === "configured" ? "text-amber-400"
                        : "text-slate-500"
                    }`}>
                      {t.whatsapp_status === "connected" ? "✓ conectado"
                       : t.whatsapp_status === "configured" ? "configurado"
                       : "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-300 tabular-nums">{(t.messages_total || 0).toLocaleString("pt-BR")}</td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <span className="text-slate-300">{fmtTokens(t.tokens_total)}</span>
                    {(t.ai_calls || 0) > 0 && (
                      <div className="text-[10px] text-slate-500">{t.ai_calls} chamadas</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <span className={(t.mrr_cents || 0) > 0 ? "text-emerald-400" : "text-slate-500"}>
                      {(t.mrr_cents || 0) > 0 ? fmtBRL(t.mrr_cents) : "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <span className={(t.packs_revenue_cents || 0) > 0 ? "text-emerald-400" : "text-slate-500"}>
                      {(t.packs_revenue_cents || 0) > 0 ? fmtBRL(t.packs_revenue_cents) : "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    <span className={(t.cost_brl || 0) > 50 ? "text-rose-400" : "text-slate-300"}>
                      {fmtBRLn(t.cost_brl)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {(t.mrr_cents || 0) > 0 ? (
                      <div>
                        <div className={margin >= 0 ? "text-emerald-400 font-semibold" : "text-rose-400 font-semibold"}>
                          {fmtBRLn(margin)}
                        </div>
                        {t.margin_pct != null && (
                          <div className={`text-[10px] ${margin >= 0 ? "text-emerald-500/70" : "text-rose-500/70"}`}>
                            {t.margin_pct}%
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-[12px]">{timeAgo(t.last_activity)}</td>
                  <td className="px-4 py-3">
                    <ArrowRight size={12} weight="regular" className="text-slate-600" />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
